import { fetchData } from '@/lib/api/fetchData';
import type { CustomFilterApiKey, CustomFilters, NewFilters } from '@/lib/filters/types';

import { createFilterParams } from './createFilterParams';

import type { GetCustomFiltersAPIResponse, CustomFilterOptionAPIResponse } from '../types/filters/customFilters';

type CustomFilterByApiKey = Record<string, { items: CustomFilterOptionAPIResponse; name: string }>;

function toCamelCase(value: string) {
  return value.replace(/_([a-z0-9])/gi, (_, char: string) => char.toUpperCase());
}

function stripGroupPrefix(apiKey: string, groupName: string) {
  const prefix = `${groupName}_`;

  if (apiKey.toLowerCase().startsWith(prefix.toLowerCase())) {
    return apiKey.slice(prefix.length);
  }

  return apiKey;
}

export function parseCustomFiltersResponse(
  customFiltersByApiKey: CustomFilterByApiKey,
  groupName: string
): CustomFilters {
  return Object.entries(customFiltersByApiKey).reduce<CustomFilters>((filters, [apiKey, filter]) => {
    const key = toCamelCase(stripGroupPrefix(apiKey, groupName));

    filters[key] = {
      apiKey: apiKey as CustomFilterApiKey,
      items: filter.items,
      name: filter.name,
    };

    return filters;
  }, {});
}

async function fetchCompanyCustomFilters(group: string, filters: NewFilters) {
  const params = createFilterParams({ filters, group }, { includeLang: false });
  return fetchData<GetCustomFiltersAPIResponse>('get_custom_filters/', { params }, false);
}

function createCustomFilterRequest(key: string, filters: NewFilters): NewFilters {
  return {
    ...filters,
    [key as CustomFilterApiKey]: [],
  };
}

async function fetchCustomFilterOptionsByKey({
  filters,
  group,
  key,
}: {
  group: string;
  key: string;
  filters: NewFilters;
}) {
  const params = {
    key,
    ...createFilterParams(
      {
        filters: createCustomFilterRequest(key, filters),
        group,
      },
      { includeLang: false }
    ),
  };

  return fetchData<CustomFilterOptionAPIResponse>('custom_filter/', { params }, true);
}

export async function fetchCustomFilters(
  group: string,
  groupName: string,
  filters: NewFilters
): Promise<CustomFilters> {
  const companyCustomFilterKeys = await fetchCompanyCustomFilters(group, filters);

  const entries = await Promise.all(
    (companyCustomFilterKeys ?? []).map(async (filter) => {
      const filterOptions = await fetchCustomFilterOptionsByKey({
        filters,
        group,
        key: filter.key,
      });

      return [
        filter.key,
        {
          items: filterOptions ?? [],
          name: filter.name,
        },
      ] as const;
    })
  );

  return parseCustomFiltersResponse(Object.fromEntries(entries), groupName);
}
